import { type ReactElement, useEffect, useSyncExternalStore } from 'react';
import { permissionGranted } from '@vello/rbac';
import { tailOf, wordsOfGrant, type AdminRuntime, type KnownGrants } from '../../src/index';
import { useAdmin } from '../session';

/**
 * D619 (owner, 2026-10-18: "só um código gigante, não mostra o nome das pessoas"): the one way a console screen writes a
 * PERSON it only knows by account id — a driver, a courier, a rider, an operator who acted. The ids a render asks for in
 * the same tick are batched into one `POST /v1/ops/users/names` (≤ 200 per call) and cached for the session, so a page of
 * rows costs one request, not one per row. The server answers only the accounts the operator may read
 * (`account:view` at the account's country); an id it leaves out keeps its short tail, with the whole id on hover.
 * Names only: the phone stays behind the audited eye (`RevealContact`).
 */

const MAX_IDS = 200;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface NameStore {
  /** id → name; `null` once the server answered without it (unknown, or not readable by this operator). */
  readonly names: Map<string, string | null>;
  readonly pending: Set<string>;
  readonly listeners: Set<() => void>;
  scheduled: boolean;
  version: number;
}

const stores = new WeakMap<AdminRuntime, NameStore>();

function storeOf(runtime: AdminRuntime): NameStore {
  let s = stores.get(runtime);
  if (s === undefined) {
    s = { names: new Map(), pending: new Set(), listeners: new Set(), scheduled: false, version: 0 };
    stores.set(runtime, s);
  }
  return s;
}

function settle(s: NameStore, entries: Iterable<readonly [string, string | null]>): void {
  for (const [id, name] of entries) s.names.set(id, name);
  s.version += 1;
  for (const l of s.listeners) l();
}

async function flush(runtime: AdminRuntime, s: NameStore): Promise<void> {
  s.scheduled = false;
  const ids = [...s.pending];
  s.pending.clear();
  for (let i = 0; i < ids.length; i += MAX_IDS) {
    const chunk = ids.slice(i, i + MAX_IDS);
    const res = await runtime.adminUsers.names(chunk);
    // A transient failure caches nothing (the next screen that shows these ids asks again); a refusal is an answer.
    if (!res.ok && res.error.status !== 403) continue;
    const found = new Map(res.ok ? (res.value.names ?? []).map((n) => [n.id, n.name] as const) : []);
    settle(s, chunk.map((id) => [id, found.get(id) ?? null] as const));
  }
}

function request(runtime: AdminRuntime, s: NameStore, id: string): void {
  if (!UUID_RE.test(id) || s.names.has(id) || s.pending.has(id)) return;
  s.pending.add(id);
  if (s.scheduled) return;
  s.scheduled = true;
  queueMicrotask(() => void flush(runtime, s));
}

/**
 * D854 (L6c-F08): whether a names lookup can answer anything for this operator. Known grants with no `account:view` at any scope (FINANCE, for one)
 * would only earn a 403 per screen, so nothing is asked and the short ids stand. Unknown grants (still loading, or the read failed) ask as before -
 * the server decides.
 */
function mayReadNames(grants: KnownGrants): boolean {
  return grants === undefined || grants.some((g) => permissionGranted(wordsOfGrant(g), 'account:view'));
}

/** The display name of one account id (`undefined` while unknown, or when the operator may not read it). */
export function usePersonName(id: string | null | undefined): string | undefined {
  const { runtime, grants } = useAdmin();
  const ask = mayReadNames(grants);
  const s = storeOf(runtime);
  useSyncExternalStore(
    (l) => {
      s.listeners.add(l);
      return () => s.listeners.delete(l);
    },
    () => s.version,
  );
  useEffect(() => {
    if (id != null && ask) request(runtime, s, id);
  }, [runtime, s, id, ask]);
  return id != null ? (s.names.get(id) ?? undefined) : undefined;
}

/**
 * The display names of SEVERAL account ids at once, for a screen that writes them inside translated strings (a trail, a
 * "proposed by …" line) where a component cannot go: one subscription, one batched request, a lookup `(id) => name | undefined`.
 */
export function usePersonNames(ids: readonly (string | null | undefined)[]): (id: string | null | undefined) => string | undefined {
  const { runtime, grants } = useAdmin();
  const ask = mayReadNames(grants);
  const s = storeOf(runtime);
  useSyncExternalStore(
    (l) => {
      s.listeners.add(l);
      return () => s.listeners.delete(l);
    },
    () => s.version,
  );
  const key = ids.filter((x): x is string => x != null).join(',');
  useEffect(() => {
    if (!ask) return;
    for (const id of key === '' ? [] : key.split(',')) request(runtime, s, id);
  }, [runtime, s, key, ask]);
  return (id) => (id != null ? (s.names.get(id) ?? undefined) : undefined);
}

/** A person by account id: the name when the operator may read it, else the short id (the whole id on hover). */
export function PersonName({ id, fallback }: { readonly id: string | null | undefined; readonly fallback?: string }): ReactElement {
  const name = usePersonName(id);
  if (id == null || id === '') return <span>{fallback ?? '—'}</span>;
  if (name !== undefined) return <span title={id}>{name}</span>;
  return (
    <span className="tnum" title={id}>
      {fallback ?? tailOf(id)}
    </span>
  );
}
